import { forwardRef, useState, useCallback, useEffect, useMemo } from 'react';

import { useRouteMatchMetricCardFilter } from 'Deer/metricCards/hooks/useRouteMatchMetricCardFilter';
import { resetMetricCardNavigateMetaData } from 'Deer/slice/MetricCardNavigateMetaData.slice';
import { ColDef, FirstDataRenderedEvent } from 'ag-grid-community';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { useAgGridPersistColumnState } from 'components/hooks/useAgGridPersistColumnState';
import { useAgGridPersistExpandOrCollapseAll } from 'components/hooks/useAgGridPersistExpandOrCollapseAll';
import { useAgGridPersistFilterModel } from 'components/hooks/useAgGridPersistFilterModel';
import produce from 'immer';
import { omit } from 'lodash';
import { useDispatch } from 'react-redux';

import { handleColumnStateDifferencesWithLS } from 'constants/agGridHelpers';
import { isPersistedFilterModelValid } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';

import { AgGridSortingFilter } from 'uiToolkitV1/AgGridSortingFilter';

import { AgGridFilterButtons } from './AgGridFilterButtons';

export interface AgGridWrapperProps extends AgGridReactProps {
  useFilterModelPersistence?: boolean;
  useColumnStatePersistence?: boolean;
  saveColumnSortInfo?: boolean;
  useExpandOrCollapseAllPersistence?: boolean;
  gridStatePersistenceKey?: string;
  getUIOptionsForLastColumn?: (param) => React.FC | null;
  resetFilterModel?: () => {};
  className?: string;
}

const clearFilter = ({ persistedFilterModel, applicableFilterKeysToClear }) =>
  omit(persistedFilterModel, applicableFilterKeysToClear);

const insertSortingFilter = (columnDef: ColDef): ColDef => {
  if (!columnDef) return {};

  // @ts-expect-error - children is not in ColDef type
  if (columnDef.children) {
    return produce(columnDef, (defaultColDef) => {
      // @ts-expect-error - children is not in ColDef type
      // eslint-disable-next-line no-param-reassign
      defaultColDef.children = defaultColDef.children.map((childColDef) => insertSortingFilter(childColDef));
    });
  }

  if (!columnDef.filter) return columnDef;

  if (columnDef.filter === 'agMultiColumnFilter') {
    return produce(columnDef, (defaultColDef) => {
      if (!defaultColDef.filterParams) {
        defaultColDef.filterParams = {
          filters: [],
        };
      } else if (!defaultColDef.filterParams.filters) {
        defaultColDef.filterParams.filters = [];
      }
      if (defaultColDef?.sortable) {
        defaultColDef.filterParams.filters.unshift({
          filter: AgGridSortingFilter,
        });
      }
      defaultColDef.filterParams.filters.push({
        filter: AgGridFilterButtons,
      });
    });
  }

  return produce(columnDef, (defaultColDef) => {
    defaultColDef.filterParams = {
      filters: [
        ...(defaultColDef?.sortable
          ? [
              {
                filter: AgGridSortingFilter,
              },
            ]
          : []),
        {
          filter: defaultColDef.filter,
          filterParams: defaultColDef.filterParams,
        },
        {
          filter: AgGridFilterButtons,
        },
      ],
    };
    defaultColDef.filter = 'agMultiColumnFilter';
  });
};

// wrapper for including a sorting filter in the filters menu
export const AgGridWrapper = forwardRef<AgGridReact, AgGridWrapperProps>((props, ref) => {
  // Override default columns to include Sorting Filter
  const defaultColDef = useMemo<ColDef>(() => insertSortingFilter(props.defaultColDef), [props.defaultColDef]);
  const dispatch = useDispatch();
  const [isMetricCardFilterApplied, setIsMetricCardFilterApplied] = useState(false);

  // Override column defs to include Sorting Filter
  const columnDefs = useMemo<ColDef[]>(() => {
    if (!props.columnDefs) return [];

    const columnDefsWithSorting = props.columnDefs.map((columnDef: ColDef) => insertSortingFilter(columnDef));

    return columnDefsWithSorting;
  }, [props.columnDefs]);

  // Override auto group columns to include Sorting Filter
  const autoGroupColumnDef = useMemo<ColDef>(
    () => insertSortingFilter(props.autoGroupColumnDef),
    [props.autoGroupColumnDef]
  );

  const gridRef = ref;
  const gridApi = gridRef?.current?.api;

  const {
    gridStatePersistenceKey = null,
    useFilterModelPersistence = true,
    useColumnStatePersistence = true,
    useExpandOrCollapseAllPersistence = true,
    saveColumnSortInfo = false,
    onFirstDataRendered,
    onFilterChanged,
    onColumnMoved,
    onColumnVisible,
  } = props;

  const persistFilters = useFilterModelPersistence && gridStatePersistenceKey;
  const persistColumns = useColumnStatePersistence && gridStatePersistenceKey;
  const persistExpandOrCollapseAll = useExpandOrCollapseAllPersistence && gridStatePersistenceKey;

  const { applicableFiltersToSet, applicableFilterKeysToClear } = useRouteMatchMetricCardFilter({
    gridStatePersistenceKey,
  });

  const { saveAppliedFilterModel, applyPersistedFilterModel, applyCustomFilterModel, persistedFilterModel } =
    useAgGridPersistFilterModel({
      gridRef,
      key: gridStatePersistenceKey,
    });

  const { persistedColumnState, saveColumnState } = useAgGridPersistColumnState({
    gridRef,
    key: gridStatePersistenceKey,
    saveSortOrder: saveColumnSortInfo,
  });

  const { saveExpandOrCollapseAllState, loadExpandedCollapseAllState } = useAgGridPersistExpandOrCollapseAll({
    gridRef,
    key: gridStatePersistenceKey,
  });

  // @ts-ignore
  // console.log(ref?.current);

  const handleOnFirstDataRendered = (ev: FirstDataRenderedEvent<any, any>) => {
    // Skip usual grid state persistence if applicableFiltersToSet is present in case of navigation through Metric Cards
    // cause the filters are already applied through useEffect
    if (isMetricCardFilterApplied) {
      return;
    }
    if (persistFilters && isPersistedFilterModelValid({ colDefs: [], persistedFilterModel: {} })) {
      applyPersistedFilterModel();
    }

    if (persistExpandOrCollapseAll) {
      loadExpandedCollapseAllState();
    }

    if (typeof onFirstDataRendered === 'function') onFirstDataRendered(ev);
  };

  const handleFilterChanged = useCallback(
    (ev) => {
      if (persistFilters) {
        // console.log('filter changed');
        saveAppliedFilterModel();
      }
      if (typeof onFilterChanged === 'function') onFilterChanged(ev);
    },
    [onFilterChanged, persistFilters, saveAppliedFilterModel]
  );

  const columnDefsToUse = useMemo(() => {
    let columnDefsToUse = columnDefs;

    if (persistColumns && Array.isArray(persistedColumnState)) {
      /* following removes first column from ColumnsState (which is grouped column) */
      const columnsStateWithoutAutoGroupColumn = persistedColumnState.filter(
        ({ colId }) => colId !== 'ag-Grid-AutoColumn'
      );
      columnDefsToUse = handleColumnStateDifferencesWithLS({
        columnsList: columnDefs,
        columnsListLS: columnsStateWithoutAutoGroupColumn,
      });
    }

    return columnDefsToUse;
  }, [columnDefs, persistColumns, persistedColumnState]);

  const handleColumnMoved = useCallback(
    (ev) => {
      if (persistColumns) {
        saveColumnState();
      }

      if (typeof onColumnMoved === 'function') onColumnMoved(ev);
    },
    [onColumnMoved, persistColumns, saveColumnState]
  );

  const handleColumnVisible = useCallback(
    (ev) => {
      if (persistColumns) {
        saveColumnState();
      }

      if (typeof onColumnVisible === 'function') onColumnVisible(ev);
    },
    [onColumnVisible, persistColumns, saveColumnState]
  );

  const handleExpandOrCollapseAll = useCallback(
    (ev) => {
      const isCollapsedAllEvent = ev?.source === 'collapseAll';
      const isExpandAllEvent = ev?.source === 'expandAll';

      if (isCollapsedAllEvent) {
        saveExpandOrCollapseAllState(false);
      }

      if (isExpandAllEvent) {
        saveExpandOrCollapseAllState(true);
      }
    },
    [saveExpandOrCollapseAllState]
  );

  useEffect(() => {
    if (!gridApi) return;
    if (applicableFiltersToSet) {
      setIsMetricCardFilterApplied(true);

      // persisting is done automatically in handleFilterChanged
      applyCustomFilterModel(applicableFiltersToSet);

      dispatch(resetMetricCardNavigateMetaData());
    }

    if (applicableFilterKeysToClear) {
      const filtersAfterClearing = clearFilter({
        persistedFilterModel,
        applicableFilterKeysToClear,
      });
      applyCustomFilterModel(filtersAfterClearing);
      dispatch(resetMetricCardNavigateMetaData());
    }
  }, [
    applicableFilterKeysToClear,
    applicableFiltersToSet,
    applyCustomFilterModel,
    dispatch,
    gridApi,
    persistedFilterModel,
  ]);

  // Pagination page size selector logic
  const paginationPageSizeSelector = useMemo(() => {
    // In mobile view, disable pagination page size selector
    if (isMobileView()) {
      return false;
    }

    // If paginationPageSizeSelector is provided from props, use that
    if (props.paginationPageSizeSelector !== undefined) {
      return props.paginationPageSizeSelector;
    }

    // Check if paginationPageSize prop exists
    if (props.paginationPageSize !== undefined) {
      const defaultOptions = [10, 20, 50, 100];
      const pageSize = props.paginationPageSize;

      // Check where pageSize lies in the default options and create selector accordingly
      if (defaultOptions.includes(pageSize)) {
        // If the pageSize is in the default options, use the full array
        return defaultOptions;
      }

      // If pageSize is not in default options, add it and sort
      const customOptions = [...defaultOptions, pageSize].sort((a, b) => a - b);
      return customOptions;
    }

    // Default case: use standard options
    return [10, 20, 50, 100];
  }, [props.paginationPageSizeSelector, props.paginationPageSize]);

  return (
    <AgGridReact
      {...props}
      className={`${props?.className ?? ''} ag-grid-wrapper`}
      defaultColDef={defaultColDef}
      columnDefs={columnDefsToUse}
      autoGroupColumnDef={autoGroupColumnDef}
      paginationPageSizeSelector={paginationPageSizeSelector}
      ref={ref}
      onFirstDataRendered={handleOnFirstDataRendered}
      onFilterChanged={handleFilterChanged}
      onColumnMoved={handleColumnMoved}
      onColumnVisible={handleColumnVisible}
      onExpandOrCollapseAll={handleExpandOrCollapseAll}
      columnMenu="legacy"
      // rowModelType="clientSide"
      suppressGroupRowsSticky
    />
  );
});
